import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface OpenBucketsData {
  assessment: number;
  rootCauseAnalysis: number;
  resolved: number;
}

interface ProblemBucketsChartProps {
  data: OpenBucketsData;
  onBucketClick?: (bucket: string) => void;
}

const bucketLabels: Record<string, string> = {
  assessment: 'Assessment',
  rootCauseAnalysis: 'Root Cause Analysis',
  resolved: 'Resolved',
};

const bucketColors: Record<string, string> = {
  assessment: 'rgba(245, 158, 11, 0.8)',      // amber
  rootCauseAnalysis: 'rgba(147, 51, 234, 0.8)', // purple
  resolved: 'rgba(16, 185, 129, 0.8)',         // green
};

const ProblemBucketsChart: React.FC<ProblemBucketsChartProps> = ({ data, onBucketClick }) => {
  const bucketKeys = Object.keys(bucketLabels);
  const labels = bucketKeys.map(key => bucketLabels[key]);
  const values = bucketKeys.map(key => data[key as keyof OpenBucketsData] || 0);
  const colors = bucketKeys.map(key => bucketColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Problems',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} problems (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 14,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value,
      }
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBucketClick) {
        const index = elements[0].index;
        const bucketKey = bucketKeys[index];
        // Map bucketKey to API parameter
        const bucketApiMap: Record<string, string> = {
          assessment: 'assessment',
          rootCauseAnalysis: 'rca',
          resolved: 'resolved',
        };
        onBucketClick(bucketApiMap[bucketKey]);
      }
    },
  };

  return (
    <div id="chart-problem-buckets" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Open Problem Buckets</h3>
        <span className="text-xs text-gray-500">Click to drill down</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        {bucketKeys.map((bucket, index) => (
          <button
            key={bucket}
            onClick={() => {
              const bucketApiMap: Record<string, string> = {
                assessment: 'assessment',
                rootCauseAnalysis: 'rca',
                resolved: 'resolved',
              };
              onBucketClick?.(bucketApiMap[bucket]);
            }}
            className="p-3 rounded-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: bucketColors[bucket] + '20', borderLeft: `4px solid ${bucketColors[bucket].replace('0.8', '1')}` }}
          >
            <div className="text-2xl font-bold" style={{ color: bucketColors[bucket].replace('0.8', '1') }}>
              {values[index]}
            </div>
            <div className="text-xs text-gray-600">{bucketLabels[bucket]}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProblemBucketsChart;
